/* =========================================================================
 * CLEAR THIS CROWN — the network half, and ONLY the network half.
 *
 * Every rule about what qualifies, what the response means and what to patch
 * lives in `clearCrown.js`, which imports nothing so a verify suite can execute
 * it under Node. This file exists because `lib/api.js` reads `import.meta.env`,
 * and one file importing it is better than every rule being unrunnable. Same
 * split as `canvaGap.js` beside `canvaSource.js`.
 *
 * ⚠️ WHY THIS IS A LOOP AND NOT A CALL — the silent no-op this build is about.
 *
 * `ids` narrows AFTER the page walk, not before it. `repairDefects` pages the
 * whole championship object and skips every record not in the set, walking at
 * most `maxPages` × 100 records per call — the server clamps maxPages to 25, so
 * 2,500 — against an object of ~14,900. A record on page 100 is simply never
 * reached, and the call comes back HTTP 200, `written: 0`, `done: false`, with
 * a cursor.
 *
 * Read as an outcome, that response says "the record did not qualify". It says
 * no such thing. So the run resumes from `nextAfter` until the record is
 * written or the walk genuinely finishes, and running out of rounds is reported
 * as its own third thing rather than being rounded to either end.
 * ========================================================================= */

import { api } from '../../../lib/api.js'
import { clearRequest, clearBefore, readClearResult, unfinished, MAX_ROUNDS } from './clearCrown.js'

export async function clearCrownRun(record, { onProgress } = {}) {
  const id = String(record?.id || '')
  if (!id) throw new Error('No record to correct.')

  const before = clearBefore(record)
  let after = null

  for (let round = 1; round <= MAX_ROUNDS; round += 1) {
    onProgress && onProgress({ round, of: MAX_ROUNDS })
    /* Every round is a real audited POST through lib/api.js, so the write
     * observer files the trail whichever round lands the write. */
    const res = await api.championsRepairDefects(clearRequest(id, after), { before })

    const verdict = readClearResult(res, id)
    if (verdict.kind !== 'more') return { ...verdict, rounds: round, before }
    after = verdict.after
  }

  return { ...unfinished(), before }
}
